import React, { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { LayoutDashboard, Users, MessageSquareQuote, FileText, Inbox, LogOut, Menu, X, Handshake, KeyRound, Briefcase, ShieldCheck, Sparkles, FileLock2, Sun, FilePen, ListChecks, UserPlus, BarChart3, Settings, PhoneCall } from "lucide-react";
import { PerlaLogo } from "./PerlaLogo";
import { useAuth } from "@/contexts/AuthContext";
import { ChangePasswordModal } from "./ChangePasswordModal";

const ALL_ITEMS = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true, roles: ["admin"] },
  { to: "/admin/venditori", label: "Venditori", icon: UserPlus, roles: ["admin"] },
  { to: "/admin/report", label: "Report vendite", icon: BarChart3, roles: ["admin"] },
  { to: "/admin/home", label: "Home", icon: LayoutDashboard, end: true, roles: ["operatore"] },
  { to: "/admin/mio-giorno", label: "Il mio giorno", icon: Sun, roles: ["operatore"] },
  { to: "/admin/setting", label: "Setting · Prima call", icon: PhoneCall, roles: ["operatore", "setter"] },
  { to: "/admin/contratti", label: "Contratti", icon: FilePen, roles: ["operatore"] },
  { to: "/admin/report-kpi", label: "Report KPI", icon: BarChart3, roles: ["operatore"] },
  { to: "/admin/setup", label: "Setup account", icon: Settings, roles: ["operatore"] },
  { to: "/admin/servizi-contratto", label: "Servizi contratto", icon: ListChecks, roles: ["admin"] },
  { to: "/admin/artists", label: "Artisti", icon: Users, roles: ["admin"] },
  { to: "/admin/testimonials", label: "Testimonianze", icon: MessageSquareQuote, roles: ["admin"] },
  { to: "/admin/team", label: "Team & Partner", icon: Handshake, roles: ["admin"] },
  { to: "/admin/content", label: "Contenuti", icon: FileText, roles: ["admin"] },
  { to: "/admin/valori", label: "Valori Perla", icon: Sparkles, roles: ["admin"] },
  { to: "/admin/leads", label: "Lead", icon: Inbox, roles: ["admin"] },
  { to: "/admin/credenziali-crm", label: "Credenziali CRM", icon: ShieldCheck, roles: ["admin"] },
  { to: "/admin/report-privacy", label: "Report Privacy", icon: FileLock2, roles: ["admin"] },
];

export const AdminLayout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showPwdModal, setShowPwdModal] = useState(false);

  const role = user?.role || "admin";
  const isAdmin = role === "admin";
  const ITEMS = ALL_ITEMS.filter((it) => it.roles.includes(role));

  // Redirect operatore away from admin-only routes to Il mio giorno
  useEffect(() => {
    if (!user) return;
    if (isAdmin) return;
    const allowed = ITEMS.map((i) => i.to);
    const currentPath = location.pathname;
    const isOnAllowed = allowed.some((p) => currentPath === p || currentPath.startsWith(p + "/"));
    if (!isOnAllowed) navigate(role === "setter" ? "/admin/setting" : "/admin/home", { replace: true });
  }, [user, isAdmin, location.pathname, navigate]);

  const handleLogout = () => {
    logout();
    navigate("/admin/login");
  };

  // Impersonation banner state
  const [impRep, setImpRep] = useState(null);
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("perla.impersonatingSalesRep");
      setImpRep(raw ? JSON.parse(raw) : null);
    } catch (e) { setImpRep(null); }
  }, [location.pathname]);
  const stopImpersonate = () => {
    sessionStorage.removeItem("perla.impersonatingSalesRep");
    setImpRep(null);
    navigate("/admin/venditori");
  };

  return (
    <div className="min-h-screen bg-[#FAFAF7] flex flex-col" data-testid="admin-layout">
      {isAdmin && impRep && (
        <div className="bg-[#FF5A1F] text-white px-6 py-2 flex items-center justify-between text-sm sticky top-0 z-40" data-testid="impersonate-banner">
          <div className="flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-widest font-bold">👁️ Stai visualizzando come</span>
            <span className="font-display uppercase">{impRep.name || impRep.email}</span>
          </div>
          <button onClick={stopImpersonate} className="text-[10px] uppercase tracking-widest font-bold border border-white/40 px-2 py-1 hover:bg-white hover:text-[#FF5A1F]" data-testid="stop-impersonate">
            Esci dalla vista
          </button>
        </div>
      )}
      <div className="flex flex-1">
      {/* Sidebar desktop */}
      <aside className="hidden lg:flex flex-col w-64 bg-[#0A0A0A] text-[#FAFAF7] border-r border-[#2A2A2A]">
        <div className="px-6 py-6 border-b border-[#2A2A2A]">
          <PerlaLogo light />
          <div className="mt-3 text-[10px] uppercase tracking-widest text-[#FF5A1F]">
            {isAdmin ? "CMS Admin" : `Operatore · ${user?.name || ""}`}
          </div>
        </div>
        <nav className="flex-1 py-6 px-3">
          {ITEMS.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.end}
              data-testid={`admin-nav-${it.label.toLowerCase()}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 mb-1 text-sm font-semibold uppercase tracking-wider transition-colors ${
                  isActive
                    ? "bg-[#FF5A1F] text-[#FAFAF7]"
                    : "text-[#FAFAF7]/70 hover:bg-[#1A1A1A] hover:text-[#FAFAF7]"
                }`
              }
            >
              <it.icon size={16} />
              {it.label}
            </NavLink>
          ))}
          <Link
            to="/crm"
            data-testid="admin-nav-crm-link"
            className={`mt-6 flex items-center justify-between gap-2 px-4 py-3 border-2 border-[#FF5A1F]/40 text-[#FF5A1F] hover:bg-[#FF5A1F] hover:text-[#FAFAF7] text-xs font-bold uppercase tracking-widest transition-colors ${isAdmin ? "" : "hidden"}`}
          >
            <span className="flex items-center gap-2"><Briefcase size={14} /> Vai al CRM</span>
            <span aria-hidden>→</span>
          </Link>
        </nav>
        <div className="p-4 border-t border-[#2A2A2A] space-y-2">
          <div className="text-xs text-[#FAFAF7]/50 mb-2">{user?.email}</div>
          <button
            type="button"
            onClick={() => setShowPwdModal(true)}
            data-testid="admin-change-password-btn"
            className="w-full flex items-center gap-2 px-4 py-2 border border-[#2A2A2A] hover:border-[#FF5A1F] hover:text-[#FF5A1F] text-xs font-bold uppercase tracking-widest"
          >
            <KeyRound size={14} /> Cambia password
          </button>
          <button
            type="button"
            onClick={handleLogout}
            data-testid="admin-logout-btn"
            className="w-full flex items-center gap-2 px-4 py-2 border border-[#2A2A2A] hover:border-[#FF5A1F] hover:text-[#FF5A1F] text-xs font-bold uppercase tracking-widest"
          >
            <LogOut size={14} /> Logout
          </button>
        </div>
      </aside>

      {/* Mobile topbar */}
      <div className="lg:hidden fixed top-0 inset-x-0 z-40 bg-[#0A0A0A] text-[#FAFAF7] flex items-center justify-between px-4 h-14 border-b border-[#2A2A2A]">
        <PerlaLogo light />
        <button
          type="button"
          onClick={() => setMobileOpen((v) => !v)}
          data-testid="admin-mobile-toggle"
        >
          {mobileOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {mobileOpen && (
        <div className="lg:hidden fixed top-14 inset-x-0 z-40 bg-[#0A0A0A] text-[#FAFAF7] p-4 border-b border-[#2A2A2A]">
          {ITEMS.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.end}
              onClick={() => setMobileOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 text-sm uppercase tracking-wider ${
                  isActive ? "text-[#FF5A1F]" : "text-[#FAFAF7]/80"
                }`
              }
              data-testid={`admin-mobile-nav-${it.label.toLowerCase()}`}
            >
              <it.icon size={16} /> {it.label}
            </NavLink>
          ))}
          <Link
            to="/crm"
            onClick={() => setMobileOpen(false)}
            data-testid="admin-mobile-nav-crm-link"
            className={`mt-3 flex items-center gap-2 px-4 py-3 border-2 border-[#FF5A1F]/40 text-[#FF5A1F] text-xs font-bold uppercase tracking-widest ${isAdmin ? "" : "hidden"}`}
          >
            <Briefcase size={14} /> Vai al CRM
          </Link>
          <button
            onClick={() => { setMobileOpen(false); setShowPwdModal(true); }}
            className="mt-3 w-full flex items-center gap-2 px-4 py-3 border border-[#2A2A2A] text-xs uppercase tracking-widest"
            data-testid="admin-mobile-change-password"
          >
            <KeyRound size={14} /> Cambia password
          </button>
          <button
            onClick={handleLogout}
            className="mt-2 w-full flex items-center gap-2 px-4 py-3 border border-[#2A2A2A] text-xs uppercase tracking-widest"
            data-testid="admin-mobile-logout"
          >
            <LogOut size={14} /> Logout
          </button>
        </div>
      )}

      <main className="flex-1 lg:ml-0 pt-14 lg:pt-0 min-w-0">
        <Outlet />
      </main>
      </div>

      {showPwdModal && <ChangePasswordModal onClose={() => setShowPwdModal(false)} />}
    </div>
  );
};
